// NOT-542 — Business Units (Phase 1). A per-tenant grouping a Job Type rolls up
// to. Mirrors the operations BusinessUnit entity returned by /business-units.

export type BusinessUnitStatus = 'ACTIVE' | 'ARCHIVED';

/** Customer-segment axis: a unit is RESIDENTIAL or COMMERCIAL, never both. */
export type BusinessUnitSegment = 'RESIDENTIAL' | 'COMMERCIAL';

export const BUSINESS_UNIT_SEGMENT_LABELS: Record<BusinessUnitSegment, string> = {
  RESIDENTIAL: 'Residential',
  COMMERCIAL: 'Commercial',
};

export const BUSINESS_UNIT_SEGMENT_HINTS: Record<BusinessUnitSegment, string> = {
  RESIDENTIAL: 'Homes only',
  COMMERCIAL: 'Businesses only',
};

/** A per-tenant business unit (from GET /business-units). */
export interface BusinessUnit {
  id: string;
  tenantId: string;
  name: string;
  /** Short unique code (e.g. "HVAC-N"). */
  code: string;
  /** Linked Trade this unit operates under. */
  tradeId: string | null;
  status: BusinessUnitStatus;
  /** Which customers this unit serves; a job type listed under several units
   *  picks one by the customer's segment. */
  segment: BusinessUnitSegment;
  /** Coverage ZIPs this unit serves (the location/branch axis), assigned
   *  like a technician's service areas. Empty = every location. */
  serviceAreaZips: string[];
  /** Badge color — hex string (defaults to '#888888' server-side). */
  color: string;
  createdAt: string;
  updatedAt: string;
}

/** One live reference that still pins a unit, by name. */
export interface BusinessUnitArchiveReference {
  id: string;
  name: string;
}

/** One kind of live reference: the real total plus the first few by name. */
export interface BusinessUnitArchiveSection<T> {
  count: number;
  sample: T[];
}

/**
 * GET /business-units/:id/archive-impact — everything archiving the unit
 * would detach: ACTIVE job types listing it, live members, ACTIVE capacity
 * rules scoped to it, and open (non-terminal) jobs stamped to it. Completed
 * jobs are never touched. Each section carries its real `count` and a capped
 * `sample` for display; `total` is the sum. `fingerprint` digests every
 * referencing id — echo it back on archive so the server can refuse if the
 * set changed after the operator reviewed it.
 */
export interface BusinessUnitArchiveImpact {
  jobTypes: BusinessUnitArchiveSection<BusinessUnitArchiveReference>;
  users: BusinessUnitArchiveSection<BusinessUnitArchiveReference>;
  capacityRules: BusinessUnitArchiveSection<BusinessUnitArchiveReference>;
  openJobs: BusinessUnitArchiveSection<{ id: string; jobNumber: string; name: string }>;
  total: number;
  fingerprint: string;
}

/** 409 `code` when the reviewed impact no longer matches the live set. */
export const ARCHIVE_IMPACT_CHANGED = 'ARCHIVE_IMPACT_CHANGED';

/**
 * POST /business-units/:id/archive body. Without `detachReferences` the
 * server refuses (409, with the impact in `references`) while anything live
 * still points at the unit; with it, the references are detached in the same
 * transaction as the archive — provided `expectedFingerprint` (from the
 * impact the operator reviewed) still matches, else 409 ARCHIVE_IMPACT_CHANGED.
 */
export interface ArchiveBusinessUnitInput {
  id: string;
  detachReferences?: boolean;
  expectedFingerprint?: string;
}

/** Status filter accepted by GET /business-units?status=. */
export type BusinessUnitStatusFilter = 'active' | 'archived' | 'all';

/** POST /business-units body. name required; tradeId optional/null for a BU
 *  with no trade; code auto-derived; color optional. */
export interface CreateBusinessUnitInput {
  name: string;
  code?: string;
  tradeId?: string | null;
  color?: string;
  /** Required: Residential, Commercial, or ANY (both). */
  segment: BusinessUnitSegment;
  serviceAreaZips?: string[];
}

/**
 * PATCH /business-units/:id body. All fields optional. Status is NOT patchable
 * here — archiving goes through POST /business-units/:id/archive. Providing
 * serviceAreaZips replaces the unit's whole coverage assignment set.
 */
export interface UpdateBusinessUnitInput {
  name?: string;
  code?: string;
  tradeId?: string | null;
  color?: string;
  segment?: BusinessUnitSegment;
  serviceAreaZips?: string[];
}
